import { hc } from 'hono/client';
import { nanoid } from 'nanoid';
import hash from 'object-hash';

import {
  type ClientToServerWebsocketEvent,
  type ServerToClientWebsocketEvent,
} from '@mai/types';

import { logger } from '@utils/logger';

let ws: WebSocket | null;

let topicHandlers: {
  subscriptionId: string;
  data: ClientToServerWebsocketEvent['data'];
  handler: (event: ServerToClientWebsocketEvent) => void;
}[] = [];

function startWebsocket() {
  const client = hc(
    import.meta.env.MODE === 'development'
      ? 'http://localhost:3001'
      : 'https://api.us.catalyzed.ai',
  );
  ws = client.ws.$ws(0);
  ws.addEventListener('open', () => {
    logger.info({}, 'WebSocket connection opened');

    // Need to synchronize subscriptions with the server on open (aka reconnect or first connect race condition)
    topicHandlers.forEach((subscription) => {
      switch (subscription.data.topic) {
        case 'ping': {
          sendServerEvent({
            type: 'subscribe',
            data: { topic: 'ping', options: {} },
          });
          break;
        }
        case 'conversation_message_stream_updated': {
          const conversationId = subscription.data.options.conversationId;
          sendServerEvent({
            type: 'subscribe',
            data: {
              topic: 'conversation_message_stream_updated',
              options: { conversationId },
            },
          });
          break;
        }
        case 'resource_updated': {
          const resourceType = subscription.data.options.resourceType;
          const resourceId = subscription.data.options.resourceId;
          sendServerEvent({
            type: 'subscribe',
            data: {
              topic: 'resource_updated',
              options: { resourceType, resourceId },
            },
          });
          break;
        }
      }
    });
  });

  ws.addEventListener('close', () => {
    logger.info({}, 'WebSocket connection closed');
    ws = null;
  });

  ws.addEventListener('message', (event) => {
    logger.info({ event }, 'WebSocket message');
    const data = JSON.parse(event.data) as ServerToClientWebsocketEvent;
    switch (data.type) {
      case 'ping': {
        topicHandlers
          .filter((subscription) => subscription.data.topic === 'ping')
          .forEach((subscription) => {
            subscription.handler(data);
          });
        break;
      }
      case 'conversation_message_stream_updated': {
        topicHandlers
          .filter(
            (subscription) =>
              subscription.data.topic === 'conversation_message_stream_updated',
          )
          .forEach((subscription) => subscription.handler(data));
        break;
      }
      case 'resource_updated': {
        topicHandlers
          .filter(
            (subscription) => subscription.data.topic === 'resource_updated',
          )
          .forEach((subscription) => subscription.handler(data));
        break;
      }
      case 'subscription_confirmed': {
        // Handle subscription confirmation - this is a server-only event
        logger.info({ data }, 'Subscription confirmed');
        break;
      }
      case 'error': {
        // Handle error - this is a server-only event
        logger.error({ data }, 'WebSocket error received');
        break;
      }
    }
  });
}

// Initial connection setup
startWebsocket();

// Periodically reconnect the websocket
setInterval(() => {
  setTimeout(() => {
    if (!ws) {
      startWebsocket();
      logger.debug({}, 'Reconnecting WebSocket');
    } else {
      logger.debug({}, 'WebSocket connection already open');
    }
  }, 100);
}, 2000);

function sendServerEvent(event: ClientToServerWebsocketEvent) {
  try {
    ws?.send(JSON.stringify(event));
  } catch (e) {
    logger.warn({ e }, 'Error sending subscription request');
  }
}

/**
 * Generalized hook for components to subscribe to particular topics, events will be passed according to the type
 */
export function useWebsocketPubSub() {
  return {
    subscribe: <T extends ClientToServerWebsocketEvent['data']>(
      { topic, options }: T,
      handler: (event: ServerToClientWebsocketEvent) => void,
    ) => {
      // ID for this subscription instance, needed for managing handlers
      const id = nanoid();
      switch (topic) {
        case 'ping': {
          const message: ClientToServerWebsocketEvent = {
            type: 'subscribe',
            data: { topic: 'ping', options: {} },
          };
          topicHandlers.push({
            subscriptionId: id,
            data: message.data,
            handler,
          });
          sendServerEvent(message);
          return id;
        }
        case 'conversation_message_stream_updated': {
          const conversationId = options.conversationId;
          const message: ClientToServerWebsocketEvent = {
            type: 'subscribe',
            data: {
              topic: 'conversation_message_stream_updated',
              options: { conversationId },
            },
          };
          topicHandlers.push({
            subscriptionId: id,
            data: message.data,
            handler,
          });
          sendServerEvent(message);
          return id;
        }
        case 'resource_updated': {
          const resourceType = options.resourceType;
          const resourceId = options.resourceId;
          const message: ClientToServerWebsocketEvent = {
            type: 'subscribe',
            data: {
              topic: 'resource_updated',
              options: { resourceType, resourceId },
            },
          };
          topicHandlers.push({
            subscriptionId: id,
            data: message.data,
            handler,
          });
          sendServerEvent(message);
          return id;
        }
      }
      throw new Error(`Unknown topic: ${topic}`);
    },
    unsubscribe: (id: string) => {
      const topicHandlerToRemove = topicHandlers.find(
        (subscription) => subscription.subscriptionId === id,
      );
      if (!topicHandlerToRemove) {
        return;
      }
      topicHandlers = topicHandlers.filter(
        (subscription) => subscription.subscriptionId !== id,
      );
      const shouldSendUnsubscribe = topicHandlers.filter(({ data }) => {
        return hash(data) === hash(topicHandlerToRemove.data);
      });
      if (shouldSendUnsubscribe.length > 0) {
        sendServerEvent({
          type: 'unsubscribe',
          data: topicHandlerToRemove.data,
        });
      }
    },
  };
}
